/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IDropdownOption, IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Link } from '@fluentui/react/lib/Link';
import { ISpinnerStyleProps, ISpinnerStyles, Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { Intent } from 'src/luis/modules/intents/Intent';
import { TestPaneSectionLabel } from 'src/luis/projects/testPane/components/common/TestPaneSectionLabel';
import { UtteranceIntent } from 'src/luis/projects/utterances/types/Utterance';

export type IntentInspectorProps = {
    /**
     * The utterance text to display as a title.
     */
    utteranceText: string;

    /**
     * The current selected intent.
     */
    selectedIntent: UtteranceIntent;

    /**
     * All intents of the current application.
     */
    allIntents: Intent[];

    /**
     * The predicted intents returned with
     * utterance predictions.
     */
    predictedIntents: UtteranceIntent[];

    /**
     * Flag determines the mode of the intent
     * whether in change mode or not.
     */
    isReadOnly?: boolean;
};

const messages = defineMessages({
    editButton: {
        id: 'testPane.intentInspector.editButton',
        defaultMessage: 'Edit'
    },
    topScoringIntent: {
        id: 'testPane.intentInspector.topScoringIntent',
        defaultMessage: 'Top-scoring intent'
    },
    assignToIntent: {
        id: 'testPane.intentInspector.assignToIntent',
        defaultMessage: 'Assign to a new intent'
    },
    dropdownPlaceholder: {
        id: 'testPane.intentInspector.dropdownPlaceholder',
        defaultMessage: 'Select intent'
    },
    loading: {
        id: 'testPane.intentInspector.loading',
        defaultMessage: 'Loading...'
    }
});

const theme = getScopedTheme('IntentInspector');
const spinnerStyles = classNamesFunction<ISpinnerStyleProps, ISpinnerStyles>()({ root: { paddingLeft: '8px' } });
const actionButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { fontSize: 12, lineHeight: 16, width: 120, color: `${theme.intentsDropdownTextColor} !important` }
});
const intentNameStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { paddingTop: 8 } });
const dropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({
    root: { width: 'calc(100% - 2px)', marginTop: 5 },
    caretDown: { fontSize: '10px', lineHeight: '32px', color: `${theme.intentsDropdownTextColor} !important` },
    title: { fontSize: '12px', lineHeight: '32px', backgroundColor: 'transparent', color: `${theme.intentsDropdownTextColor} !important` }
});

/**
 * Represents the intent segment in the utterance inspector.
 *
 * @param props The input properties for the inspector.
 */
export const IntentInspector = observer((props: IntentInspectorProps) => {
    const { utteranceText, selectedIntent, allIntents, predictedIntents, isReadOnly } = props;

    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();
    const versionedAppId = useCurrentVersionedAppId();
    const [isIntentSelectionVisible, setIsIntentSelectionVisible] = React.useState(false);
    const addUtteranceTracker = useProgressTracker({ onComplete: () => setIsIntentSelectionVisible(false) });

    let intentsOptions: IDropdownOption[] = [];

    /**
     * Calculate the possible intent options to add the
     * utterance to in case of rendering in not a read-only
     * mode.
     */
    if (!isReadOnly) {
        intentsOptions = allIntents
            .map(intent => predictedIntents.find(pI => pI.intent.id === intent.id))
            .sort((a, b) => (a.score < b.score ? 1 : -1))
            .map(pI => ({
                key: pI.intent.id,
                text: pI.intent.name,
                title: `${pI.intent.name} (${pI.score.toFixed(3)})`,
                data: { secondaryText: `(${pI.score.toFixed(3)})` }
            }));
    }

    /**
     * Adds the current test utterance to
     * the selected intent.
     *
     * @param intentId The id of the intent to assign
     * the utterance to.
     */
    const onIntentChange = (intentId: string) => {
        const newIntent = allIntents.find(i => i.id === intentId);

        dispatcher.dispatch('addUtterances', {
            ...versionedAppId,
            model: newIntent,
            newUtterances: true,
            tracker: addUtteranceTracker,
            utterances: [{ text: utteranceText, labeledEntities: [], tokenizedText: [] }]
        });
    };

    return (
        <Stack verticalAlign="center">
            <TestPaneSectionLabel>{localize(messages.topScoringIntent)}</TestPaneSectionLabel>

            <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                <LuisText data-automation-id="selectedIntentName" lineHeight="16px" styles={intentNameStyles}>
                    {selectedIntent.intent.name} ({selectedIntent.score.toFixed(3)})
                </LuisText>

                {!isReadOnly && (
                    <Stack tokens={{ padding: '8px 0 0 0' }}>
                        <Link styles={actionButtonStyles} onClick={() => setIsIntentSelectionVisible(true)}>
                            {localize(messages.assignToIntent)}
                        </Link>

                        {isIntentSelectionVisible && (
                            <Stack horizontalAlign="start">
                                <LuisDropdown
                                    orderByKey="none"
                                    autoSelect={false}
                                    styles={dropdownStyles}
                                    options={intentsOptions}
                                    calloutProps={{ calloutWidth: 300 }}
                                    disabled={addUtteranceTracker?.isRunning}
                                    placeholder={localize(messages.dropdownPlaceholder)}
                                    onChange={(_, o) => onIntentChange(o.key as string)}
                                />

                                {addUtteranceTracker?.isRunning && (
                                    <Spinner
                                        labelPosition="right"
                                        styles={spinnerStyles}
                                        size={SpinnerSize.xSmall}
                                        label={localize(messages.loading)}
                                    />
                                )}
                            </Stack>
                        )}
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
});
